import { PRODUCT_EVENTS, useAnalytics } from "@repo/analytics";
import { ApiError } from "@repo/api-client";
import { useTranslation } from "@repo/i18n";
import { css } from "@repo/styled-system/css";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useLocation } from "react-router";
import { PageScaffold } from "../../shell/PageScaffold";
import { useVenue } from "../../shell/VenueContext";
import { trialApi } from "../billing/trialApi";
import { FirstBookingPanel } from "../first-booking/FirstBookingPanel";
import { useBookableTimes } from "../schedule/bookability";
import { NoBookableTimesAlert } from "../schedule/NoBookableTimesAlert";
import {
  firstIncompleteStep,
  onboardingApi,
  onboardingKeys,
  STEP_ORDER,
  type OnboardingProgressResponse,
  type OnboardingStep,
  type WizardLocationState,
} from "./onboardingApi";
import {
  AddressStep,
  CapacityStep,
  HoursStep,
  ListingAuthStep,
  LinkStep,
  ProfileStep,
} from "./OnboardingSteps";

/**
 * The resumable onboarding wizard (`/v/:venueId/onboarding`, tasks/venue-onboarding.md). A checklist
 * sidebar fed by `GET /venues/{id}/onboarding` (out-of-order navigation), a step shell mounting each
 * owning feature's surface (PROFILE/CAPACITY/LISTING_AUTH inline; hours/photos/address/staff link to
 * their editors), resume-at-first-incomplete (R6), and a completion screen that moves the venue to
 * READY (never LIVE) then shows the preview-mode banner + "request activation" CTA (R2/R7). All copy
 * resolves through the FR locale bundle (ADR-0013).
 */
/** The Activation step is a synthetic final entry (not one of the 7 §C.1 steps) — the "go live" surface. */
type Selected = OnboardingStep | "ACTIVATION";

export function OnboardingWizard() {
  const { venueId } = useVenue();
  const { t } = useTranslation();
  const qc = useQueryClient();
  const location = useLocation();
  const [selected, setSelected] = useState<Selected | null>(null);
  // Set while the owner fixes missing hours from the go-live warning: saving returns them to activation.
  const [returnToActivation, setReturnToActivation] = useState(false);

  const progressQuery = useQuery({
    queryKey: onboardingKeys.progress(venueId),
    queryFn: () => onboardingApi.getProgress(venueId),
  });

  // Resume: land on the Activation step once the venue is READY (or routed here from the setup banner);
  // otherwise the first incomplete step (R6). Derived directly during render (the condition becomes
  // false, and stays false, the instant `selected` is set, so there is no risk of a render loop).
  const routedToActivation = (location.state as WizardLocationState | null)?.activation === true;
  if (selected === null && progressQuery.data) {
    const ready =
      progressQuery.data.lifecycle === "READY" || progressQuery.data.lifecycle === "LIVE";
    setSelected(
      routedToActivation || ready ? "ACTIVATION" : firstIncompleteStep(progressQuery.data.steps),
    );
  }

  // The banner also shows on this page, so a fresh routing must move an already-mounted wizard — adjusted
  // during render (comparing against the last-seen location.state) rather than via an effect.
  const [lastLocationState, setLastLocationState] = useState(location.state);
  if (location.state !== lastLocationState) {
    setLastLocationState(location.state);
    if (routedToActivation) selectStep("ACTIVATION");
  }

  function selectStep(step: Selected) {
    setSelected(step);
    setReturnToActivation(false);
  }

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: onboardingKeys.progress(venueId) });
    void qc.invalidateQueries({ queryKey: onboardingKeys.capacity(venueId) });
    void qc.invalidateQueries({ queryKey: onboardingKeys.listingAuth(venueId) });
  };

  const complete = useMutation({
    mutationFn: () => onboardingApi.complete(venueId),
    onSuccess: refresh,
  });

  if (progressQuery.isLoading || !progressQuery.data || selected === null) {
    return (
      <PageScaffold title={t("onboarding.title")}>
        <p>{t("onboarding.loading")}</p>
      </PageScaffold>
    );
  }

  const progress = progressQuery.data;
  const isReady = progress.lifecycle === "READY" || progress.lifecycle === "LIVE";

  return (
    <PageScaffold title={t("onboarding.title")}>
      <div
        className={css({
          display: "grid",
          gridTemplateColumns: { base: "1fr", md: "260px 1fr" },
          gap: "6",
          alignItems: "start",
        })}
      >
        <Checklist
          progress={progress}
          selected={selected}
          onSelect={selectStep}
          isLive={progress.lifecycle === "LIVE"}
        />
        <section className={css({ display: "flex", flexDirection: "column", gap: "4" })}>
          {selected === "ACTIVATION" ? (
            <>
              <h2 className={css({ fontSize: "lg", fontWeight: "semibold" })}>
                {t("onboarding.activate.step")}
              </h2>
              {isReady ? (
                <ActivationPanel
                  venueId={venueId}
                  lifecycle={progress.lifecycle}
                  onLive={refresh}
                  onFixHours={() => {
                    setSelected("HOURS");
                    setReturnToActivation(true);
                  }}
                />
              ) : (
                <CompletionPanel
                  canComplete={progress.mandatoryComplete}
                  missing={progress.missingMandatory}
                  onComplete={() => complete.mutate()}
                  completing={complete.isPending}
                  error={
                    complete.error instanceof ApiError
                      ? completionErrorText(complete.error, t)
                      : null
                  }
                />
              )}
            </>
          ) : (
            <>
              <h2 className={css({ fontSize: "lg", fontWeight: "semibold" })}>
                {t(`onboarding.step.${selected}`)}
              </h2>
              <StepContent
                step={selected}
                venueId={venueId}
                progress={progress}
                onSaved={() => {
                  refresh();
                  if (returnToActivation) selectStep("ACTIVATION");
                }}
              />
            </>
          )}
        </section>
      </div>
    </PageScaffold>
  );
}

function Checklist({
  progress,
  selected,
  onSelect,
  isLive,
}: {
  progress: OnboardingProgressResponse;
  selected: Selected;
  onSelect: (s: Selected) => void;
  isLive: boolean;
}) {
  const { t } = useTranslation();
  return (
    <nav aria-label={t("onboarding.checklist")}>
      <ol
        className={css({ display: "flex", flexDirection: "column", gap: "1", listStyle: "none" })}
      >
        {STEP_ORDER.map((step) => {
          const done = progress.steps[step] === "COMPLETE";
          const active = step === selected;
          return (
            <li key={step}>
              <button
                type="button"
                onClick={() => onSelect(step)}
                aria-current={active ? "step" : undefined}
                className={css({
                  display: "flex",
                  alignItems: "center",
                  gap: "2",
                  w: "full",
                  textAlign: "left",
                  px: "3",
                  py: "2",
                  borderRadius: "md",
                  fontSize: "sm",
                  cursor: "pointer",
                  bg: active ? "colorPalette.subtle" : "transparent",
                  fontWeight: active ? "semibold" : "normal",
                  _hover: { bg: "bg.muted" },
                })}
              >
                <span aria-hidden className={css({ color: done ? "green.default" : "fg.muted" })}>
                  {done ? "✓" : "○"}
                </span>
                <span>{t(`onboarding.step.${step}`)}</span>
                {progress.mandatorySteps.includes(step) && (
                  <span className={css({ fontSize: "xs", color: "fg.muted", ml: "auto" })}>
                    {t("onboarding.required")}
                  </span>
                )}
                <span className={css({ srOnly: true })}>
                  {done ? t("onboarding.state.done") : t("onboarding.state.todo")}
                </span>
              </button>
            </li>
          );
        })}
        {/* Activation — the synthetic final step (go live). */}
        <li>
          <button
            type="button"
            onClick={() => onSelect("ACTIVATION")}
            aria-current={selected === "ACTIVATION" ? "step" : undefined}
            className={css({
              display: "flex",
              alignItems: "center",
              gap: "2",
              w: "full",
              textAlign: "left",
              px: "3",
              py: "2",
              borderRadius: "md",
              fontSize: "sm",
              cursor: "pointer",
              bg: selected === "ACTIVATION" ? "colorPalette.subtle" : "transparent",
              fontWeight: selected === "ACTIVATION" ? "semibold" : "normal",
              _hover: { bg: "bg.muted" },
            })}
          >
            <span aria-hidden className={css({ color: isLive ? "green.default" : "fg.muted" })}>
              {isLive ? "✓" : "○"}
            </span>
            <span>{t("onboarding.activate.step")}</span>
          </button>
        </li>
      </ol>
    </nav>
  );
}

function StepContent({
  step,
  venueId,
  progress,
  onSaved,
}: {
  step: OnboardingStep;
  venueId: string;
  progress: OnboardingProgressResponse;
  onSaved: () => void;
}) {
  switch (step) {
    case "PROFILE":
      return <ProfileStep venueId={venueId} version={progress.venueVersion} onSaved={onSaved} />;
    case "CAPACITY":
      return <CapacityStep venueId={venueId} onSaved={onSaved} />;
    case "LISTING_AUTH":
      return <ListingAuthStep venueId={venueId} onSaved={onSaved} />;
    case "HOURS":
      return <HoursStep venueId={venueId} onSaved={onSaved} />;
    case "ADDRESS":
      return (
        <AddressStep venueId={venueId} venueVersion={progress.venueVersion} onSaved={onSaved} />
      );
    case "PHOTOS":
      return <LinkStep progress={progress} step="PHOTOS" to="../settings/media" />;
    case "STAFF":
      return <LinkStep progress={progress} step="STAFF" to="../settings" />;
    default:
      return null;
  }
}

function CompletionPanel({
  canComplete,
  missing,
  onComplete,
  completing,
  error,
}: {
  canComplete: boolean;
  missing: OnboardingStep[];
  onComplete: () => void;
  completing: boolean;
  error: string | null;
}) {
  const { t } = useTranslation();
  return (
    <div className={css({ display: "flex", flexDirection: "column", gap: "2" })}>
      {!canComplete && (
        <p className={css({ fontSize: "sm", color: "fg.muted" })}>
          {t("onboarding.completion.missing", {
            steps: missing.map((s) => t(`onboarding.step.${s}`)).join(", "),
          })}
        </p>
      )}
      {error && (
        <p role="alert" className={css({ fontSize: "sm", color: "red.default" })}>
          {error}
        </p>
      )}
      <button
        type="button"
        disabled={!canComplete || completing}
        onClick={onComplete}
        className={css({
          bg: "colorPalette.default",
          color: "colorPalette.fg",
          px: "5",
          py: "2",
          borderRadius: "md",
          fontWeight: "semibold",
          fontSize: "sm",
          cursor: "pointer",
          w: "fit-content",
          _disabled: { opacity: 0.5, cursor: "not-allowed" },
        })}
      >
        {t("onboarding.completion.cta")}
      </button>
    </div>
  );
}

const activateBtn = css({
  bg: "colorPalette.default",
  color: "colorPalette.fg",
  px: "5",
  py: "2",
  borderRadius: "md",
  fontWeight: "semibold",
  fontSize: "sm",
  cursor: "pointer",
  w: "fit-content",
  _disabled: { opacity: 0.5, cursor: "not-allowed" },
});
const gateBtn = css({
  px: "4",
  py: "2",
  borderRadius: "md",
  borderWidth: "1px",
  borderColor: "border.default",
  fontSize: "sm",
  cursor: "pointer",
  w: "fit-content",
  _disabled: { opacity: 0.5, cursor: "not-allowed" },
});

/**
 * Guided self-service activation (READY → LIVE). "Mettre en ligne" attempts the go-live gates; a blocked
 * attempt returns `GO_LIVE_BLOCKED { unmetGates }`, and we surface a one-click fix for the two the owner
 * can satisfy — accept the DPA and start the free trial — each of which re-attempts go-live. The subdomain
 * gate is auto-provisioned server-side, so it shouldn't surface here. Both go-live entry points end here
 * (the setup banner routes to this panel), so the no-bookable-times warning can't be bypassed.
 */
function ActivationPanel({
  venueId,
  lifecycle,
  onLive,
  onFixHours,
}: {
  venueId: string;
  lifecycle: string;
  onLive: () => void;
  onFixHours: () => void;
}) {
  const { t } = useTranslation();
  const [unmet, setUnmet] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const isLive = lifecycle === "LIVE";
  const { isLoading: checking, bookability } = useBookableTimes(venueId);
  const analytics = useAnalytics();
  const warned = !isLive && bookability !== null && bookability !== "OK" ? bookability : null;

  useEffect(() => {
    if (warned) analytics.capture(PRODUCT_EVENTS.goLiveNoHoursWarned, { reason: warned });
  }, [analytics, warned]);

  const attempt = useMutation({
    mutationFn: () => onboardingApi.goLive(venueId),
    onSuccess: () => {
      setUnmet(null);
      setError(null);
      onLive();
    },
    onError: (err) => {
      const problem =
        err instanceof ApiError
          ? (err.problem as { errorCode?: string; unmetGates?: string[] } | undefined)
          : undefined;
      if (problem?.errorCode === "GO_LIVE_BLOCKED") {
        setUnmet(problem.unmetGates ?? []);
        setError(null);
      } else {
        setError(t("onboarding.error.generic"));
      }
    },
  });
  const acceptDpa = useMutation({
    mutationFn: () => onboardingApi.acceptDpa(venueId),
    onSuccess: () => attempt.mutate(),
    onError: () => setError(t("onboarding.error.generic")),
  });
  const startTrial = useMutation({
    mutationFn: () => trialApi.startSelfServe(),
    onSuccess: () => attempt.mutate(),
    onError: (err) =>
      setError(
        err instanceof ApiError && err.status === 403
          ? t("onboarding.activate.trialRootOnly")
          : t("onboarding.error.generic"),
      ),
  });

  const busy = attempt.isPending || acceptDpa.isPending || startTrial.isPending;
  const needs = (needle: string) => (unmet ?? []).some((g) => g.toLowerCase().includes(needle));

  if (isLive) {
    return (
      <div className={css({ display: "flex", flexDirection: "column", gap: "4" })}>
        <output
          className={css({
            display: "block",
            bg: "green.subtle",
            color: "green.text",
            border: "1px solid",
            borderColor: "green.muted",
            borderRadius: "md",
            px: "4",
            py: "3",
            fontSize: "sm",
          })}
        >
          <strong>{t("onboarding.activate.live")}</strong>
          {/* Not a promise we can keep when nothing is bookable; the panel's warning says so instead. */}
          {(bookability === "OK" || bookability === null) && (
            <p>{t("onboarding.activate.liveHint")}</p>
          )}
        </output>
        <FirstBookingPanel onFixHours={onFixHours} />
      </div>
    );
  }

  return (
    <div className={css({ display: "flex", flexDirection: "column", gap: "3" })}>
      {error && (
        <p role="alert" className={css({ fontSize: "sm", color: "red.default" })}>
          {error}
        </p>
      )}
      {(needs("dpa") || needs("subscription")) && (
        <div
          className={css({
            display: "flex",
            flexDirection: "column",
            gap: "2",
            p: "3",
            borderWidth: "1px",
            borderColor: "border.default",
            rounded: "md",
          })}
        >
          <p className={css({ fontSize: "sm", fontWeight: "medium" })}>
            {t("onboarding.activate.blockedIntro")}
          </p>
          {needs("dpa") && (
            <button
              type="button"
              disabled={busy}
              onClick={() => acceptDpa.mutate()}
              className={gateBtn}
            >
              {t("onboarding.activate.acceptDpa")}
            </button>
          )}
          {needs("subscription") && (
            <button
              type="button"
              disabled={busy}
              onClick={() => startTrial.mutate()}
              className={gateBtn}
            >
              {t("onboarding.activate.startTrial")}
            </button>
          )}
        </div>
      )}
      {warned ? (
        <NoBookableTimesAlert
          venueId={venueId}
          bookability={warned}
          busy={busy}
          onFixHours={onFixHours}
          onContinue={() => {
            analytics.capture(PRODUCT_EVENTS.goLiveNoHoursContinued, { reason: warned });
            attempt.mutate();
          }}
        />
      ) : (
        <button
          type="button"
          disabled={busy || checking}
          onClick={() => attempt.mutate()}
          className={activateBtn}
        >
          {busy ? t("onboarding.activate.working") : t("onboarding.activate.cta")}
        </button>
      )}
      <p className={css({ fontSize: "xs", color: "fg.muted" })}>{t("onboarding.activate.hint")}</p>
    </div>
  );
}

function completionErrorText(
  err: ApiError,
  t: (k: string, v?: Record<string, unknown>) => string,
): string {
  const problem = err.problem as { errorCode?: string; missingSteps?: string[] } | undefined;
  if (problem?.errorCode === "ONBOARDING_INCOMPLETE") {
    const steps = (problem.missingSteps ?? []).map((s) => t(`onboarding.step.${s}`)).join(", ");
    return t("onboarding.completion.missing", { steps });
  }
  return t("onboarding.error.generic");
}
